import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/i18n';
import { toast } from 'sonner';
import { MessageCircle, UserPlus, Users, Heart, MessageSquare, Bell } from 'lucide-react';
import { playNotificationPing } from '@/lib/notificationSound';

const NotifCtx = createContext({
  threads: [], friends: { accepted: [], pending_in: [], pending_out: [] },
  convoyRequests: [], items: [],
  totalUnread: 0, pendingInCount: 0, convoyRequestsCount: 0, activityUnread: 0,
  refresh: () => {}, friendStatus: () => 'none',
  markAllRead: () => {}, deleteItem: () => {}, clearAll: () => {},
  openChatWith: null, setOpenChatWith: () => {},
});

const TYPE_TO_PATH = (n) => {
  if (n.target_type === 'media') return `/media#${n.target_id}`;
  if (n.target_type === 'spot') return `/maps/fh6#spot=${n.target_id}`;
  if (n.target_type === 'user') return `/u/${n.actor_id}`;
  return '/';
};

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [friends, setFriends] = useState({ accepted: [], pending_in: [], pending_out: [] });
  const [convoyRequests, setConvoyRequests] = useState([]);
  const [items, setItems] = useState([]);
  const [activityUnread, setActivityUnread] = useState(0);
  const [openChatWith, setOpenChatWith] = useState(null);
  const prevUnreadMap = useRef({});
  const prevPendingIds = useRef(new Set());
  const prevConvoyReqIds = useRef(new Set());
  const prevNotifIds = useRef(new Set());
  const isFirstLoad = useRef(true);

  const refresh = useCallback(async () => {
    if (!user || user === false) return;
    try {
      const [a, b, cr, nf] = await Promise.all([
        api.get('/dm'),
        api.get('/friends'),
        api.get('/convoys/requests/pending').catch(() => ({ data: [] })),
        api.get('/notifications').catch(() => ({ data: { items: [], unread: 0 } })),
      ]);
      const newThreads = a.data || [];
      const newFriends = b.data || { accepted: [], pending_in: [], pending_out: [] };
      const newConvoyRequests = cr.data || [];
      const newItems = nf.data?.items || [];
      const newUnread = nf.data?.unread || 0;

      // Detect new incoming DMs (toast)
      let shouldPing = false;
      if (!isFirstLoad.current) {
        newThreads.forEach((th) => {
          const prevUnread = prevUnreadMap.current[th.user_id] || 0;
          if (th.unread > prevUnread && !th.last_from_me) {
            shouldPing = true;
            toast.success(`${th.name}: ${th.last_text}`, {
              icon: <MessageCircle className="w-4 h-4 text-[#00F0FF]" />,
              duration: 4500,
            });
          }
        });
        // Detect new friend requests
        const newPendingIds = new Set(newFriends.pending_in.map((f) => f.id));
        newFriends.pending_in.forEach((f) => {
          if (!prevPendingIds.current.has(f.id)) {
            shouldPing = true;
            toast(`${f.name} ${t('notif.friend_request')}`, {
              icon: <UserPlus className="w-4 h-4 text-[#FF007F]" />,
              duration: 5000,
            });
          }
        });
        prevPendingIds.current = newPendingIds;

        // Detect new convoy join requests
        const newConvoyReqIds = new Set();
        newConvoyRequests.forEach((cv) => {
          (cv.requests || []).forEach((r) => {
            const key = `${cv.convoy_id}:${r.user_id}`;
            newConvoyReqIds.add(key);
            if (!prevConvoyReqIds.current.has(key)) {
              shouldPing = true;
              toast(`${r.name} ${t('notif.convoy_request') || 'wants to join'} ${cv.convoy_name}`, {
                icon: <Users className="w-4 h-4 text-[#FFD479]" />,
                duration: 10000,
                action: {
                  label: t('notif.manage') || 'Gérer',
                  onClick: () => navigate(`/convoys/${cv.convoy_id}`),
                },
              });
            }
          });
        });
        prevConvoyReqIds.current = newConvoyReqIds;

        // Activity notifications (likes/comments/reactions/follows)
        const newNotifIds = new Set(newItems.map((n) => n.id));
        newItems.forEach((n) => {
          if (!prevNotifIds.current.has(n.id)) {
            shouldPing = true;
            const msg = (() => {
              switch (n.type) {
                case 'spot_like':
                  return `${n.actor_name} a aimé ton spot "${n.target_title}"`;
                case 'spot_comment':
                  return `${n.actor_name} a commenté ton spot "${n.target_title}"`;
                case 'media_like':
                  return `${n.actor_name} a aimé ta publication "${n.target_title}"`;
                case 'media_comment':
                  return `${n.actor_name} a commenté ta publication "${n.target_title}"`;
                case 'media_reaction':
                  return `${n.actor_name} a réagi ${n.extra?.emoji || ''} à "${n.target_title}"`;
                case 'follow':
                  return `${n.actor_name} te suit maintenant`;
                default:
                  return `${n.actor_name}`;
              }
            })();
            const Icon = ['spot_like', 'media_like'].includes(n.type)
              ? Heart
              : ['spot_comment', 'media_comment'].includes(n.type)
                ? MessageSquare
                : n.type === 'follow' ? UserPlus : Bell;
            toast(msg, {
              icon: <Icon className="w-4 h-4 text-[#FF007F]" />,
              duration: 6000,
              action: {
                label: t('notif.see') || 'Voir',
                onClick: () => navigate(TYPE_TO_PATH(n)),
              },
            });
          }
        });
        prevNotifIds.current = newNotifIds;
        if (shouldPing) playNotificationPing();
      } else {
        prevPendingIds.current = new Set(newFriends.pending_in.map((f) => f.id));
        const initIds = new Set();
        newConvoyRequests.forEach((cv) => (cv.requests || []).forEach((r) => initIds.add(`${cv.convoy_id}:${r.user_id}`)));
        prevConvoyReqIds.current = initIds;
        prevNotifIds.current = new Set(newItems.map((n) => n.id));
        isFirstLoad.current = false;
      }
      prevUnreadMap.current = Object.fromEntries(newThreads.map((th) => [th.user_id, th.unread]));

      setThreads(newThreads);
      setFriends(newFriends);
      setConvoyRequests(newConvoyRequests);
      setItems(newItems);
      setActivityUnread(newUnread);
    } catch (err) { console.warn('[Notif] refresh failed:', err); }
  }, [user, t, navigate]);

  useEffect(() => {
    if (!user || user === false) {
      setThreads([]); setFriends({ accepted: [], pending_in: [], pending_out: [] });
      setConvoyRequests([]); setItems([]); setActivityUnread(0);
      isFirstLoad.current = true;
      prevUnreadMap.current = {}; prevPendingIds.current = new Set();
      prevConvoyReqIds.current = new Set();
      prevNotifIds.current = new Set();
      return;
    }
    refresh();
    const i = setInterval(refresh, 15000);
    return () => clearInterval(i);
  }, [user, refresh]);

  const totalUnread = threads.reduce((s, t) => s + (t.unread || 0), 0);
  const pendingInCount = friends.pending_in.length;
  const convoyRequestsCount = convoyRequests.reduce((s, c) => s + (c.requests?.length || 0), 0);

  const markAllRead = useCallback(async () => {
    if (!user || user === false) return;
    try {
      await api.post('/notifications/mark-read', {});
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setActivityUnread(0);
    } catch (err) { console.warn('[Notif] mark-all-read failed:', err); }
  }, [user]);

  const deleteItem = useCallback(async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setItems((prev) => prev.filter((n) => n.id !== id));
      setActivityUnread((u) => Math.max(0, u - 1));
    } catch (err) { console.warn('[Notif] deleteItem failed:', err); }
  }, []);

  const clearAll = useCallback(async () => {
    try {
      await api.delete('/notifications');
      setItems([]); setActivityUnread(0);
    } catch (err) { console.warn('[Notif] clearAll failed:', err); }
  }, []);

  const friendStatus = (uid) => {
    if (!uid || !user || user === false) return 'none';
    if (uid === user.id) return 'self';
    if (friends.accepted.some((f) => f.user_id === uid)) return 'accepted';
    if (friends.pending_out.some((f) => f.user_id === uid)) return 'pending_out';
    if (friends.pending_in.some((f) => f.user_id === uid)) return 'pending_in';
    return 'none';
  };

  return (
    <NotifCtx.Provider value={{
      threads, friends, convoyRequests, items,
      totalUnread, pendingInCount, convoyRequestsCount, activityUnread,
      refresh, friendStatus, markAllRead, deleteItem, clearAll,
      openChatWith, setOpenChatWith,
    }}>
      {children}
    </NotifCtx.Provider>
  );
}

export const useNotif = () => useContext(NotifCtx);
